import React, { useState, useEffect, useContext, useRef } from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { logoBackPath } from "../config";
import { NameContext } from "../context/NameContext";
import ChangeNameModal from "./ChangeNameModal";
import RoughBorder from "../kits/LayoutKit/RoughBorder";

// Helper component for button with RoughBorder
const RadialButtonWithBorder = ({ item, pos, i, onClick, className, delay = 0.05 }) => {
  const buttonRef = useRef(null);

  return (
    <motion.button
      ref={buttonRef}
      key={item.action}
      initial={{ opacity: 0, x: 0, y: 0 }}
      animate={{
        opacity: 1,
        x: pos.x,
        y: pos.y,
        transition: { duration: 0.35, ease: "easeOut", delay: i * delay },
      }}
      exit={{ opacity: 0, x: 0, y: 0, transition: { duration: 0.2 } }}
      whileHover={{ rotate: 18, scale: 1.08 }}
      className={className}
      onClick={onClick}
      style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
    >
      {Array.isArray(item.label) ? (
        <>
          <span>{item.label[0]}</span>
          <span>{item.label[1]}</span>
        </>
      ) : (
        item.label
      )}
      <RoughBorder
        parentRef={buttonRef}
        shape="circle"
        padding={2}
        color="#AC8E66"
        strokeWidth={0.18}
        roughness={0.5}
        bowing={1}
      />
    </motion.button>
  );
};

const BitterButton = ({ onQuickAction }) => {
  const { name } = useContext(NameContext);
  const [scrollY, setScrollY] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [tooltipCount, setTooltipCount] = useState(0);

  const [showRadialMenu, setShowRadialMenu] = useState(false);
  const [showContactSubMenu, setShowContactSubMenu] = useState(false);
  const [showAboutSubMenu, setShowAboutSubMenu] = useState(false);
  const [showAllSubMenu, setShowAllSubMenu] = useState(false);
  const [showLernStudioSubMenu, setShowLernStudioSubMenu] = useState(false);
  const [showChangeNameModal, setShowChangeNameModal] = useState(false);

  const controls = useAnimation();

  const OFFSET_FROM_TOP = 100;
  const TOOLTIP_DURATION = 6000;
  const MAX_TOOLTIP_SHOWS = 2;

  const anyMenuOpen =
    showRadialMenu ||
    showContactSubMenu ||
    showAboutSubMenu ||
    showAllSubMenu ||
    showLernStudioSubMenu ||
    showChangeNameModal;

  const closeAllMenus = () => {
    setShowRadialMenu(false);
    setShowContactSubMenu(false);
    setShowAboutSubMenu(false);
    setShowAllSubMenu(false);
    setShowLernStudioSubMenu(false);
    setShowChangeNameModal(false);
    setShowTooltip(false);
  };

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && closeAllMenus();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  useEffect(() => {
    document.body.style.overflow = anyMenuOpen ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [anyMenuOpen]);

  const getRadialPosition = (angle, radius = 180) => {
    const rad = (angle * Math.PI) / 180;
    return { x: Math.cos(rad) * radius, y: Math.sin(rad) * radius };
  };

  useEffect(() => {
    const tipCount = parseInt(sessionStorage.getItem("menuTooltipCount") || "0");

    const handleScroll = () => {
      const current = window.scrollY;
      setScrollY(current);

      const viewportHeight = window.innerHeight;
      const buttonHeight = 56;
      const menuOffset = anyMenuOpen ? 50 : 0;
      const radiusSpace = anyMenuOpen ? 220 : 0;

      // Calculate desired top position
      let desiredTop = current + OFFSET_FROM_TOP;

      // Calculate the bottom of the viewport
      const viewportBottom = current + viewportHeight;

      // Maximum top position to keep everything visible
      // Button should never be positioned such that it + offset + radius exceeds viewport
      const maxAllowedTop = viewportBottom - buttonHeight - radiusSpace - menuOffset - 20;

      // Limit desiredTop to maxAllowedTop
      if (desiredTop > maxAllowedTop) {
        desiredTop = maxAllowedTop;
      }

      // Add menu offset for the final position
      const finalTop = desiredTop + menuOffset;

      controls.start({
        top: finalTop,
        transition: { type: "spring", stiffness: 90, damping: 14 },
      });

      if (current > 100 && !hasScrolled && tipCount < MAX_TOOLTIP_SHOWS) {
        setHasScrolled(true);
        setShowTooltip(true);
        setTimeout(() => setShowTooltip(false), TOOLTIP_DURATION);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasScrolled, anyMenuOpen, controls]);

  const handleButtonClick = () => {
    if (anyMenuOpen) closeAllMenus();
    else setShowRadialMenu(true);
  };

  const handleRadialItemClick = (action) => {
    setShowRadialMenu(false);

    if (action === "contact") return setShowContactSubMenu(true);
    if (action === "about") return setShowAboutSubMenu(true);
    if (action === "all") return setShowAllSubMenu(true);
    if (action === "lernstudio") return setShowLernStudioSubMenu(true);

    onQuickAction?.(action);
  };

const handleSubClick = (action) => {
  closeAllMenus(); // ✅ Erst ALLE zu

  if (action === "lernstudio") {
    setShowLernStudioSubMenu(true); // ✅ dann LernStudio auf
    return;
  }

  if (action === "changename") {
    setShowChangeNameModal(true); // ✅ Namen-Ändern-Modal öffnen
    return;
  }

  onQuickAction?.(action); // ✅ sonst normalen Action-Handler
};

  const radialMenuItems = [
    { label: name ? ["Moin", name] : "Moin", action: "home", angle: 120 },
    { label: ["Einfach", "Ich"], action: "about", angle: 150 },
    { label: ["Sag", "Hallo"], action: "contact", angle: 180 },
    { label: ["Lern", "Studio"], action: "lernstudio", angle: 210 },
    { label: "Menu", action: "all", angle: 240 },
  ];

  const contactSubMenuItems = [
    { label: "EMAIL senden", action: "email", angle: 120 },
    { label: "Anrufen", action: "tel", angle: 160 },
    { label: "Lern mich kennen", action: "teams", angle: 200 },
  ];

  const aboutSubMenuItems = [
    { label: "Über mich", action: "cv", angle: 120 },
    { label: "Kompass", action: "skills", angle: 160 },
    { label: "Referenz", action: "projects", angle: 200 },
  ];

  const allSubMenuItems = [
    { label: "Namen Ändern", action: "changename", angle: 120 },
    { label: "Kontakt mit mir", action: "saghallo", angle: 160 },
    { label: "Login Kurse", action: "login", angle: 200 },
  ];

  const lernStudioSubMenuItems = [
    { label: "Youtube", action: "youtube", angle: 100 },
    { label: "Blog", action: "blog", angle: 140 },
    { label: "ZenCode", action: "zendev", angle: 180 },
    { label: "Case", action: "CaseStudy", angle: 220 },
    { label: "Kurse", action: "Kurse", angle: 260 },
  ];

  return (
    <>
      <AnimatePresence>
        {anyMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, backdropFilter: "blur(10px)" }}
            exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm"
            onClick={closeAllMenus}
          />
        )}
      </AnimatePresence>

      <motion.div
        className="fixed right-4 z-[200]"
        animate={controls}
        initial={{ top: OFFSET_FROM_TOP }}
      >
        {showTooltip && !anyMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="absolute right-16 top-1/2 -translate-y-1/2 pointer-events-none"
          >
            <div className="bg-gray-900 text-orange-50 text-xs font-mono px-3 py-2 rounded-lg shadow-lg whitespace-nowrap relative">
              Menü öffnen
              <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 
              border-y-[6px] border-y-transparent border-l-[6px] border-l-gray-900"></div>
            </div>
          </motion.div>
        )}

        <AnimatePresence>
          {showRadialMenu &&
            radialMenuItems.map((item, i) => {
              const pos = getRadialPosition(item.angle);
              return (
                <RadialButtonWithBorder
                  key={item.action}
                  item={item}
                  pos={pos}
                  i={i}
                  onClick={() => handleRadialItemClick(item.action)}
                  className="absolute z-[210] w-16 h-16 bg-gray-900 text-orange-50
                  rounded-full text-[0.6rem] font-mono shadow-lg
                  flex flex-col items-center justify-center text-center leading-tight px-1"
                  delay={0.05}
                />
              );
            })}
        </AnimatePresence>

        {[
          { state: showContactSubMenu, items: contactSubMenuItems },
          { state: showAboutSubMenu, items: aboutSubMenuItems },
          { state: showAllSubMenu, items: allSubMenuItems },
        ].map((menu, idx) => (
          <AnimatePresence key={idx}>
            {menu.state &&
              menu.items.map((item, i) => {
                const pos = getRadialPosition(item.angle, 160);
                return (
                  <RadialButtonWithBorder
                    key={item.action}
                    item={item}
                    pos={pos}
                    i={i}
                    onClick={() => handleSubClick(item.action)}
                    className="absolute z-[220] w-16 h-16 bg-gray-50
                    text-gray-900 border border-brand rounded-full text-[0.6rem] font-mono shadow-lg
                    flex items-center justify-center text-center leading-tight px-1"
                    delay={0.06}
                  />
                );
              })}
          </AnimatePresence>
        ))}

        <AnimatePresence>
          {showLernStudioSubMenu &&
            lernStudioSubMenuItems.map((item, i) => {
              const pos = getRadialPosition(item.angle, 160);
              return (
                <RadialButtonWithBorder
                  key={item.action}
                  item={item}
                  pos={pos}
                  i={i}
                  onClick={() => handleSubClick(item.action)}
                  className="absolute z-[220] w-16 h-16 bg-gray-50
                  text-gray-900 border border-brand
                  text-black rounded-full text-[0.6rem] font-mono shadow-lg
                  flex items-center justify-center text-center leading-tight px-1"
                  delay={0.06}
                />
              );
            })}
        </AnimatePresence>

        <motion.div
          className="relative z-[300] w-14 h-14 rounded-full bg-gray-50
          flex items-center justify-center border border-gray-200 shadow-lg cursor-pointer"
          onClick={handleButtonClick}
          whileHover={{ scale: 1.1 }}
        >
          <motion.div
            className="w-full h-full rounded-full overflow-hidden"
            whileHover={{ rotate: 18, scale: 1.08 }}
          >
            <img src={logoBackPath} className="w-full h-full rounded-full" alt="Menu" />
          </motion.div>

          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            {[...Array(2)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full border border-brand"
                style={{ width: "75%", height: "75%" }}
                initial={{ scale: 0.1, opacity: 0.5 }}
                animate={{ scale: 1.5, opacity: 0.35 }}
                transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
              />
            ))}
          </div>
        </motion.div>
      </motion.div>

      {showChangeNameModal && (
        <ChangeNameModal onClose={() => setShowChangeNameModal(false)} />
      )}
    </>
  );
};

export default BitterButton;
